import { UserStatus } from '@bp/bp-graphql-types';
import { ProfileRoles } from '@bp/pim-auth-constants';
import { Checkbox, DatePicker, Grid, GridColumn, GridRow, Input, ModalBottomButtons } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { toBirthdayDate } from '../../../utils/dateCalculations';
import { schema } from './PersonForm.schema';

const CreatePersonDocument = graphql(`
  mutation CreatePerson($input: BpProfileCreateInput!) {
    createBpProfile(input: $input) {
      __typename
      uuid
      salutation
      title
      prefixName
      firstName
      lastName
      email
      birthday
      organizationRoles
    }
  }
`);

const UpdatePersonDocument = graphql(`
  mutation UpdatePerson($update: BpProfileUpdateInput!, $uuid: String!) {
    updateBpProfile(update: $update, uuid: $uuid) {
      __typename
      uuid
      salutation
      title
      prefixName
      firstName
      lastName
      email
      birthday
      organizationRoles
    }
  }
`);

const PersonFormQuery = graphql(`
  query PersonFormData($organizationUuid: ID!, $personUuid: ID) {
    profiles(where: { organization: { uuid: $organizationUuid }, uuid: $personUuid }) {
      __typename
      uuid
      salutation
      title
      prefixName
      firstName
      lastName
      email
      birthday
      organizationRoles
      user {
        __typename
        uuid
        status
      }
    }
  }
`);

type PersonFormType = {
  salutation: string | null;
  title: string | null;
  prefixName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  birthday: Date | null;
  role: ProfileRoles;
  organizationRoles: string[] | null;
  invite: boolean;
};

type PersonFormProps = {
  uuid?: string;
  role: ProfileRoles;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const PersonForm = ({ uuid, role, onLoad, onClose }: PersonFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [, createPerson] = useMutation(CreatePersonDocument);
  const [, updatePerson] = useMutation(UpdatePersonDocument);

  const [{ data }] = useQuery({
    query: PersonFormQuery,
    variables: { organizationUuid, personUuid: uuid },
    pause: !uuid,
  });

  const profile = data?.profiles[0] || null;

  const initialValues: PersonFormType = {
    salutation: profile?.salutation ?? null,
    title: profile?.title ?? null,
    prefixName: profile?.prefixName ?? null,
    firstName: profile?.firstName ?? null,
    lastName: profile?.lastName ?? null,
    email: profile?.email ?? null,
    birthday: profile?.birthday ? new Date(profile.birthday) : null,
    role: role,
    organizationRoles: profile?.organizationRoles ?? null,
    invite: false,
  };

  async function handleSubmit(values: PersonFormType, helper: FormikHelpers<PersonFormType>) {
    onLoad(true);

    if (uuid) {
      // invite when email changed
      // => data and value has email AND those two mails are not the same AND the user is already invited
      const resendEmail =
        !!profile?.email &&
        !!values.email &&
        values.email !== profile.email &&
        profile.user?.status !== UserStatus.None;

      const { error } = await updatePerson({
        update: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          prefixName: values.prefixName,
          salutation: values.salutation,
          title: values.title,
          birthday: values.birthday ? toBirthdayDate(values.birthday) : null,
          invite: resendEmail || values.invite,
          frontendUri: import.meta.env.VITE_APP_REDIRECT_URI,
          organizationRoles: values.organizationRoles?.filter((r) => r !== null) ?? [],
        },
        uuid: uuid,
      });

      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessUpdateToast();
      }
    } else {
      const { error } = await createPerson({
        input: {
          organizationRoles: [values.role],
          createAndInviteUser: values.invite,
          organization: organizationUuid,
          firstName: values.firstName ?? '',
          lastName: values.lastName ?? '',
          email: values.email ?? '',
          prefixName: values.prefixName,
          salutation: values.salutation,
          title: values.title,
          birthday: values.birthday ? toBirthdayDate(values.birthday) : null,
          frontendUri: import.meta.env.VITE_APP_REDIRECT_URI,
          foreignRefId: [],
        },
      });

      if (error) {
        showUserDbErrorToast();
      } else {
        showSuccessCreateToast();
      }
    }

    helper.resetForm();
    onLoad(false);
    onClose();
  }

  return (
    <Formik<PersonFormType> onSubmit={handleSubmit} initialValues={initialValues} validationSchema={schema}>
      {({ resetForm, errors, dirty, handleChange, setFieldValue, values }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingBottom='s'>
                <GridColumn width={6}>
                  <Input
                    name='salutation'
                    label={t('persons.salutation')}
                    onChange={handleChange}
                    value={values.salutation}
                    error={errors.salutation}
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <Input name='title' label={t('persons.personTitle')} onChange={handleChange} value={values.title} />
                </GridColumn>
              </GridRow>
              <GridRow spacingBottom='s' spacingTop='s'>
                <Input
                  name='firstName'
                  label={t('common.firstName')}
                  onChange={handleChange}
                  value={values.firstName}
                  error={errors.firstName}
                />
              </GridRow>
              <GridRow spacingBottom='s' spacingTop='s'>
                <GridColumn width={4}>
                  <Input
                    name='prefixName'
                    label={t('persons.prefixName')}
                    onChange={handleChange}
                    value={values.prefixName}
                    error={errors.prefixName}
                  />
                </GridColumn>
                <GridColumn width={8}>
                  <Input
                    name='lastName'
                    label={t('common.lastName')}
                    onChange={handleChange}
                    value={values.lastName}
                    error={errors.lastName}
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingBottom='s' spacingTop='s'>
                <GridColumn width={4}>
                  <DatePicker
                    name='birthday'
                    label={t('common.birthday')}
                    onChange={(e) => setFieldValue('birthday', e?.toISOString())}
                    value={values.birthday ?? undefined}
                    showMonthYearDropdown
                  />
                </GridColumn>
                <GridColumn width={8}>
                  <Input
                    name='email'
                    label={t('common.email')}
                    onChange={handleChange}
                    type='email'
                    value={values.email}
                    error={errors.email}
                  />
                </GridColumn>
              </GridRow>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{
                disabled: !dirty,
              }}
              additionalLeftComponent={
                <Checkbox
                  name='invite'
                  label={t('profiles.invite.sendInvitation')}
                  onChange={handleChange}
                  checked={values.invite}
                  disabled={!values.email}
                />
              }
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
